import { useState, useCallback } from "react"
import { useQuery, useSubscription, useMutation } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { toast } from "sonner"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ProgressRing } from "@/components/shared/progress-ring"
import { Download, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { DeliveriesQuery, DestinationsQuery, SubscriptionsQuery } from "@/lib/graphql/queries"
import { BulkRetryDeliveriesMutation } from "@/lib/graphql/mutations"
import { DeliveryUpdatedSubscription } from "@/lib/graphql/subscriptions"
import { useAuth } from "@/hooks/use-auth"
import { exportJSON, exportCSV } from "@/lib/export"
import type { ResultOf } from "@/lib/graphql/types"
import type { DeliveryStatus } from "@/lib/generated/graphql"

type DeliveryItem = ResultOf<typeof DeliveriesQuery>["deliveries"]["nodes"][number]

const LIMIT = 25

const columns: Column<DeliveryItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.event.eventType}</code>,
  },
  {
    header: "Destination",
    cell: (row) => <span className="text-xs">{row.destination.name}</span>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <ProgressRing value={row.attempts} max={row.maxAttempts} size={28} strokeWidth={2.5} />
    ),
  },
  {
    header: "Next Attempt",
    cell: (row) =>
      row.nextAttemptAt ? <TimeAgo date={row.nextAttemptAt} /> : (
        <span className="text-xs text-muted-foreground">-</span>
      ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function DeliveriesPage() {
  const { isConfigured } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const [bulkRetryOpen, setBulkRetryOpen] = useState(false)

  const status = (searchParams.get("status") as DeliveryStatus) || undefined
  const destinationId = searchParams.get("destinationId") ?? undefined
  const subscriptionId = searchParams.get("subscriptionId") ?? undefined
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [status, destinationId, subscriptionId, search])

  const [destinations] = useQuery({ query: DestinationsQuery })
  const [subscriptions] = useQuery({ query: SubscriptionsQuery })
  const [result, reexecute] = useQuery({
    query: DeliveriesQuery,
    variables: { status, destinationId, subscriptionId, search, after: pagination.after, first: pagination.first },
  })

  const handleDeliveryUpdate = useCallback(
    (
      _prev: ResultOf<typeof DeliveryUpdatedSubscription> | undefined,
      response: ResultOf<typeof DeliveryUpdatedSubscription>,
    ) => {
      if (response?.deliveryUpdated) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: DeliveryUpdatedSubscription, pause: !isConfigured }, handleDeliveryUpdate)

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Deliveries" description="Webhook delivery attempts">
        <Button variant="outline" size="sm" onClick={() => setBulkRetryOpen(true)}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Bulk Retry
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.deliveries.nodes ?? []).map((d) => ({
                id: d.id, eventType: d.event.eventType, destination: d.destination.name,
                status: d.status, attempts: d.attempts, maxAttempts: d.maxAttempts,
                nextAttemptAt: d.nextAttemptAt, createdAt: d.createdAt,
              }))
              exportJSON(rows, "deliveries")
            }}>Export JSON</DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.deliveries.nodes ?? []).map((d) => ({
                id: d.id, eventType: d.event.eventType, destination: d.destination.name,
                status: d.status, attempts: d.attempts, maxAttempts: d.maxAttempts,
                nextAttemptAt: d.nextAttemptAt ?? "", createdAt: d.createdAt,
              }))
              exportCSV(rows, ["id", "eventType", "destination", "status", "attempts", "maxAttempts", "nextAttemptAt", "createdAt"], "deliveries")
            }}>Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={destinationId ?? "all"}
          onValueChange={(v) => setFilter("destinationId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All destinations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All destinations</SelectItem>
            {destinations.data?.destinations.nodes.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={subscriptionId ?? "all"}
          onValueChange={(v) => setFilter("subscriptionId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All subscriptions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subscriptions</SelectItem>
            {subscriptions.data?.subscriptions.nodes.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
            <SelectItem value="SUPERSEDED">Superseded</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search deliveries..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.deliveries.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.deliveries.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No deliveries"
        onRowClick={(row) => navigate(`/deliveries/${row.id}`)}
      />

      <BulkRetryDialog
        open={bulkRetryOpen}
        onOpenChange={setBulkRetryOpen}
        destinations={destinations.data?.destinations.nodes ?? []}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />
    </div>
  )
}

type DestinationOption = ResultOf<typeof DestinationsQuery>["destinations"]["nodes"][number]

function BulkRetryDialog({
  open,
  onOpenChange,
  destinations,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  destinations: DestinationOption[]
  onSuccess: () => void
}) {
  const [, bulkRetry] = useMutation(BulkRetryDeliveriesMutation)
  const [since, setSince] = useState("")
  const [until, setUntil] = useState("")
  const [destinationId, setDestinationId] = useState("all")
  const [status, setStatus] = useState<DeliveryStatus>("FAILED")
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const res = await bulkRetry({
      filter: {
        since: since ? new Date(since).toISOString() : undefined,
        until: until ? new Date(until).toISOString() : undefined,
        destinationId: destinationId === "all" ? undefined : destinationId,
        status: status || undefined,
      },
    })
    setSubmitting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success(`Retried ${res.data?.bulkRetryDeliveries.count ?? 0} deliveries`)
    onOpenChange(false)
    setSince("")
    setUntil("")
    setDestinationId("all")
    setStatus("FAILED")
    onSuccess()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk Retry Deliveries</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bulk-since">Since</Label>
            <Input
              id="bulk-since"
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bulk-until">Until</Label>
            <Input
              id="bulk-until"
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Destination</Label>
            <Select value={destinationId} onValueChange={setDestinationId}>
              <SelectTrigger>
                <SelectValue placeholder="All destinations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All destinations</SelectItem>
                {destinations.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as DeliveryStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="SUPERSEDED">Superseded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Retrying..." : "Retry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
